import { Link } from 'react-router';
import { ChevronRight, Crown, QrCode } from 'lucide-react';
import type { HomeView, StampCardView } from '@ulp/consumer-core';
import { fmtKurus, toKurus } from '@ulp/consumer-core';
import { fmtNumber } from '../lib/format';
import { useTenant } from '../lib/tenant';
import { cx, StarMark } from './kit';

/** Üyelik kartı: yıldız bakiyesi, seviye, sonraki seviyeye ilerleme, cüzdan. Kartın kendisi karekoda götürür. */
export function MembershipCard({ home, walletEnabled, className }: { home: HomeView; walletEnabled: boolean; className?: string }) {
  const { bootstrap, path } = useTenant();
  const stars = Number(home.points.spendable);
  const progress = home.tierProgress;
  return (
    <Link
      to={path('kod')}
      className={cx('card-emerald relative block overflow-hidden rounded-[28px] p-5 shadow-float transition-transform active:scale-[0.99]', className)}
      aria-label="Üyelik kartını göster"
    >
      <Guilloche />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-white/70">{bootstrap?.tenant.name}</p>
          <p className="mt-0.5 truncate text-[15px] font-bold">{home.member.fullName ?? 'Üye'}</p>
        </div>
        {home.tier && (
          <span className="gold-fill inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-extrabold uppercase tracking-[0.06em]">
            <Crown className="size-3.5" /> {home.tier.name}
          </span>
        )}
      </div>

      <div className="relative mt-6 flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <StarMark className="size-7" />
            <span className="text-[44px] font-extrabold leading-none tracking-[-0.04em] tabular">{fmtNumber(stars)}</span>
          </div>
          <p className="mt-1.5 text-[13px] font-medium text-white/65">Kullanılabilir yıldız</p>
        </div>
        <span className="grid size-12 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/15">
          <QrCode className="size-6 text-gold-bright" />
        </span>
      </div>

      {progress && home.nextTier && (
        <div className="relative mt-5">
          <div className="h-2 overflow-hidden rounded-full bg-white/12">
            <div className="gold-fill h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, Math.min(100, progress.percent))}%` }} />
          </div>
          <p className="mt-2 text-[12.5px] text-white/70">
            <span className="font-bold text-gold-bright">{fmtNumber(progress.remaining)}</span> yıldız sonra <span className="font-semibold text-white">{home.nextTier.name}</span>
          </p>
        </div>
      )}

      {walletEnabled && (
        <div className="relative mt-5 flex items-center justify-between rounded-2xl bg-black/20 px-4 py-3 ring-1 ring-white/10">
          <span className="text-[13px] font-medium text-white/70">Cüzdan bakiyesi</span>
          <span className="text-[16px] font-bold tabular">{fmtKurus(toKurus(home.wallet.spendable))}</span>
        </div>
      )}
    </Link>
  );
}

/** Kart yüzeyinde ince, pahalı hissettiren çizgi deseni (guilloché). */
function Guilloche() {
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.13]" viewBox="0 0 400 260" preserveAspectRatio="none" aria-hidden>
      {Array.from({ length: 14 }).map((_, i) => (
        <path
          key={i}
          d={`M-20 ${190 - i * 9} C 90 ${120 - i * 12}, 190 ${250 - i * 6}, 420 ${110 - i * 10}`}
          fill="none"
          stroke="url(#gl)"
          strokeWidth="0.8"
        />
      ))}
      <defs>
        <linearGradient id="gl" x1="0" x2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#EFD58B" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Damga kartı: hedef kadar kutu; dolu olanlar altın. */
export function StampCard({ card }: { card: StampCardView }) {
  const target = card.target ?? 10;
  const filled = Math.min(card.stamps, target);
  const left = Math.max(0, target - filled);
  return (
    <div className="rounded-3xl bg-surface p-5 shadow-card ring-1 ring-line/60">
      <div className="flex items-baseline justify-between gap-3">
        <p className="truncate text-[16px] font-bold tracking-[-0.01em]">{card.programName ?? 'Damga kartı'}</p>
        <p className="shrink-0 text-[13px] font-semibold text-muted tabular">
          {filled}/{target}
        </p>
      </div>
      <div className="mt-4 grid grid-cols-5 gap-2.5">
        {Array.from({ length: target }).map((_, i) => (
          <div
            key={i}
            className={cx(
              'grid aspect-square place-items-center rounded-2xl transition-all',
              i < filled ? 'gold-fill shadow-glow' : 'bg-surface-2 ring-1 ring-dashed ring-line',
              i === filled - 1 && 'animate-pop',
            )}
          >
            {i < filled ? <StarMark className="size-5 [&_path]:fill-[var(--gold-ink)]" /> : <span className="text-[12px] font-semibold text-muted/70">{i + 1}</span>}
          </div>
        ))}
      </div>
      <p className="mt-4 text-[13.5px] text-muted">
        {left === 0 ? (
          <span className="font-semibold text-success">Kart doldu — ödülün cüzdanında!</span>
        ) : (
          <>
            Ödüle <span className="font-bold text-ink">{left}</span> damga kaldı
          </>
        )}
        {card.completedCount > 0 && <span> · {card.completedCount} kez tamamlandı</span>}
      </p>
    </div>
  );
}

/** Liste satırı (Hesabım, kısa yollar). */
export function RowLink({ to, icon, title, subtitle, trailing }: { to: string; icon: React.ReactNode; title: string; subtitle?: string; trailing?: React.ReactNode }) {
  return (
    <Link to={to} className="flex items-center gap-3.5 px-4 py-3.5 transition-colors active:bg-surface-2">
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-2 text-ink-2">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold">{title}</span>
        {subtitle && <span className="block truncate text-[13px] text-muted">{subtitle}</span>}
      </span>
      {trailing}
      <ChevronRight className="size-5 shrink-0 text-muted/70" />
    </Link>
  );
}
